import moment from 'moment'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import CONFIG from '../conf/delivery.json'
import { entryScriptName } from 'init/entry-script-name'
import { isHashtagToSearchEnabled } from '../init/is-hashtag-to-search-enabled'

/**
 * Функция для склонения существительных.
 *
 * @param   {number} number Число, используемое для склонения.
 * @param   {Array}  titles Массив со склонениями слова.
 * @returns {string}        Слово в нужном склонении.
 */
export function declination (number, titles) {
  let cases = [2, 0, 1, 1, 1, 2]

  return titles[(number % 100 > 4 && number % 100 < 20) ? 2 : cases[(number % 10 < 5) ? number % 10 : 5]]
}

/**
 * (LEB) Форматирование даты
 * @param {*|Date}  value     Дата
 * @param {boolean} showTime  Показать дату
 * @returns {string}           Форматированная дата
 */
export function dateFormat (value, showTime) {
  const date = moment(value).toDate()
  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ]
  const year = date.getFullYear()
  const month = months[date.getMonth()]
  const day = date.getDate() >= 10 ? date.getDate() : '0' + date.getDate()
  const hour = date.getHours() >= 10 ? date.getHours() : '0' + date.getHours()
  const minute = date.getMinutes() >= 10 ? date.getMinutes() : '0' + date.getMinutes()

  return `${day} ${month} ${year} г. ` + (showTime ? `${hour}:${minute}` : '')
}
/**
 * Парсинг ошибок из объекта/коллекции/массива
 * @method errorParser
 * @param {![Object]|Object|string} errorData Данные для парсинга
 * @param {string}                  field     Имя поля
 * @param {string}                  formName  Имя формы (для париснга ошибки типа `payloadChunk[field]`)
 * @returns {string|null}           Строка с текстом ошибки (если !errorData - null)
 */
export const errorParser = ({ errorData = null, field = null, formName = null }) => {
  let result = null
  if (errorData && typeof errorData === 'string') {
    result = errorData
  }
  if (field) {
    const query = formName ? `${formName}[${field}]` : field
    if (isArray(errorData)) {
      errorData = errorData.find(obj => (obj.field === query)) || null
      if (errorData) {
        result = isArray(errorData.message)
          ? errorData.message[0] // иногда возвращается массив (пример: order-form)
          : errorData.message
        errorData = null
      }
    }
    if (isObject(errorData)) {
      errorData = isArray(errorData[query]) ? errorData[query][0] : errorData[query]
      if (errorData) {
        result = isObject(errorData) ? errorData.message : errorData
      }
    }
  }
  return result
}
/**
 * Сборка адреса доставки из данных заказа
 * @param {Object}  order   объект с данными заказа
 * @returns {string}          собранная строка
 */
export const compileDelivery = order => {
  const { is_jp_request, order_delivery, jp_organizer } = order
  let result
  /* eslint-disable */
  if (order_delivery) {
    const {
      delivery_type_id,
      delivery_type_name,
      settlement_name,
      street,
      house_num,
      phone,
      region_text,
      address_text,
      counterparty_delivery_address,
      counterparty_delivery_address_text
    } = order_delivery
    if (!is_jp_request) {
      result = `${delivery_type_name}`
      if (counterparty_delivery_address && counterparty_delivery_address.address) {
        result += `, ${counterparty_delivery_address.address}`
      } else if (counterparty_delivery_address_text) {
        result += `, ${counterparty_delivery_address_text}`
      } else {
        if (delivery_type_id && !CONFIG.DELIVERY_TYPE_IDS_FROM_WAREHOUSE.includes(delivery_type_id) && settlement_name) {
          result += `, г. ${settlement_name}`
        }
        if (street) {
          result += `, ${street}`
        }
        if (house_num) {
          result += `, ${house_num}`
        }
        if (region_text) {
          result += `, ${region_text}`
        }
        if (address_text) {
          result += `, ${address_text}`
        }
      }
      if (phone) {
        result += `, тел.: ${phone}`
      }
    } else {
      result = `согласовать с организатором совместной покупки `
      if (jp_organizer && jp_organizer.name) {
        result += jp_organizer.name
      }
      if (jp_organizer && jp_organizer.phone) {
        result += ` по телефону ${jp_organizer.phone}`
      }
    }
  }
  /* eslint-enable */
  return result
}

/**
 * Убираем пробелы и лишние символы из поискового запроса.
 * @param {string} query поисковый запрос
 * @returns {string} нормализованный запрос
 */
export const normalizeQuery = query => {
  return String(query)
    .trim()
    .replace(/^&|\?/g, '')
    .toLowerCase()
}

/**
 * Конкатенация названий города и региона
 * @param {Object} settlement Выбранный город.
 * @returns {string} Название города и региона
 */
export const getSettlementString = settlement => {
  let output = ''
  if (settlement) {
    output += settlement.get('name')
    if (settlement.get('region')) {
      output += ` (${settlement.get('region')})`
    }
  }

  return output
}

/**
 * Возвращает значение содержащее буквы или цифры, очищая другие символы.
 * @param {string} value Значение для преобразования.
 * @returns {string} Значение содержащее буквы или цифры.
 */
export const removeRegexpSpecialCharacter = value => value.toString().replace(/[\\,\][!:@+#$%^&*_({})\-=<>./|`;?"']/g, '')

/**
 * Проверяет равны ли все аргументы друг другу в виде строк в нижнем регистре без лишних пробелов.
 * @param {string} values значения для сравнения.
 * @returns {boolean} Булево значение, которое показывает, равны ли строки в нижнем регистре без лишних пробелов.
 */
export const isSameStrings = (...values) => {
  const strings = values.map(argument => String(argument).trim().toLowerCase())
  return strings.every(string => string === strings[0])
}

/**
 * Заменяет все сопоставимые с pattern значения
 * @param {string} string Строка.
 * @param {(string|regexp)} pattern Сопоставляемое значение.
 * @param {(string|number)} value Значение на которое производится замена.
 * @returns {string} Строка.
 */
export const replace = (string, pattern, value) => string.replace(pattern, value)

/**
 * @typedef {Object} StringPartData Объект c данными части строки.
 * @property {string} text Текстовая часть.
 * @property {string|null|undefined} href Url, если является ссылкой.
 */

/**
 * Преобразует строку с ссылками, хэштегами в массив данных.
 * @param {string} content Обрабатываемая строка.
 * @returns {Array<StringPartData>} Массив с данными.
 */
export const prepareStringWithLinks = content => {
  const simaURLregExp = /(\S*sima-land\.ru\S*)/g
  const linkRegExp = /(<a\s.*?>.*?<\/a>)/ig
  const hashtagRegExp = /(#[A-zА-Яа-яЁё0-9-]+)/m

  const allRegExp = /(<a\s.*?>.*?<\/a>)|(\S*sima-land\.ru\S*)|(#[A-zА-Яа-яЁё0-9-]+)/igm
  const separator = '__%separator%__'

  return content
    ? String(content)
      .replace(allRegExp, value => `${separator}${value}${separator}`)
      .split(separator)
      .filter(Boolean)
      .map(part => {
        let result = { text: part }
        if (linkRegExp.test(part)) {
          result = prepareLink(part)
        } else if (simaURLregExp.test(part)) {
          result.href = part
        } else if (hashtagRegExp.test(part)) {
          result = prepareHashtag(part)
        }
        return result
      })
    : [{ text: '' }]
}

/**
 * Преобразует хэштег в объект с данными.
 * @param {string} hashtag Строка вида '#test'.
 * @returns {StringPartData} Объект с данными.
 */
export const prepareHashtag = hashtag => {
  let result = { text: hashtag }
  const sidPattern = /(#([0-9]{6,}))/
  if (hashtag && isHashtagToSearchEnabled) {
    let linkQuery = hashtag
    let linkText = hashtag
    const hasSid = hashtag.match(sidPattern)
    if (hasSid) {
      linkQuery = hasSid[2]
    }
    result = {
      href: `${entryScriptName}/search/?q=${encodeURIComponent(linkQuery)}`,
      text: linkText,
    }
  }
  return result
}

/**
 * Преобразует ссылку в объект с данными.
 * @param {string} link Строка вида '<a href="#">test</a>'.
 * @returns {StringPartData} Объект с данными.
 */
export const prepareLink = link => {
  const linkHref = link.match(/href="(.*)"/)
  const linkText = link.match(/>(.*)</)
  return {
    href: linkHref ? linkHref[1] : null,
    text: linkText ? linkText[1] : '',
  }
}

/**
 * Возвращает очищенный от HTML-тегов текст.
 * @param {string} textWithHtml Текст с HTML-тегами.
 * @returns {string} Очищенный от тегов текст.
 */
export const stripHtmlTags = textWithHtml => {
  const div = document.createElement('div')
  div.innerHTML = textWithHtml
  return div.textContent || div.innerText || ''
}
